import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

/* ─── SC Analysis Tab (existing) ─── */
function SCAnalysis({ siteId }) {
  const [sessions, setSessions] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [striking, setStriking] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('opportunity_score');
  const [sortDir, setSortDir] = useState('desc');
  const [subTab, setSubTab] = useState('all');

  useEffect(() => { load(); }, [siteId]);

  async function load() {
    try {
      const data = await api.getResearchSessions(siteId);
      setSessions(data.sessions || []);
      const done = (data.sessions || []).find(s => s.status === 'completed');
      if (done) { setActiveSession(done); await loadResults(done.id); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function loadResults(sid) {
    const [res, str] = await Promise.all([
      api.getResearchResults(sid), api.getStrikingDistance(sid, 0)
    ]);
    setKeywords(res.keywords || []); setStriking(str.keywords || []);
  }

  async function run() {
    setRunning(true); setStatus('שולף נתונים מ-Search Console ו-Google Ads...');
    try {
      const r = await api.runKeywordResearch(siteId);
      const poll = setInterval(async () => {
        const s = await api.getResearchStatus(r.session_id);
        if (s.status === 'completed') {
          clearInterval(poll); setStatus(null); setRunning(false);
          setActiveSession(s); await loadResults(s.session_id); await load();
        } else if (s.status === 'failed') {
          clearInterval(poll); setStatus('שגיאה: ' + s.error_message); setRunning(false);
        }
      }, 3000);
    } catch (e) { setStatus('שגיאה: ' + e.message); setRunning(false); }
  }

  function sort(field) {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  }

  const list = (subTab === 'striking' ? striking : keywords)
    .filter(k => !search || k.keyword?.includes(search))
    .filter(k => !filterType || k.keyword_type === filterType)
    .sort((a, b) => {
      let av = a[sortBy], bv = b[sortBy];
      if (av == null) return 1; if (bv == null) return -1;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const TH = ({ label, field, w }) => (
    <th onClick={() => sort(field)} style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, fontSize: '12px', color: sortBy === field ? '#3B82F6' : '#8B99AE', cursor: 'pointer', width: w, borderBottom: '2px solid #1E293B', whiteSpace: 'nowrap' }}>
      {label} {sortBy === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const typeStyle = { top_ranking: { l: 'Top', c: '#22C55E' }, striking_distance: { l: 'Striking', c: '#F59E0B' }, existing: { l: 'קיים', c: '#3B82F6' }, new_opportunity: { l: 'חדש', c: '#A855F7' } };
  const fmtPos = p => p ? <span style={{ color: p <= 3 ? '#22C55E' : p <= 10 ? '#22D3EE' : p <= 20 ? '#F59E0B' : '#EF4444', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{p.toFixed(1)}</span> : '-';

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#8B99AE' }}>טוען...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <p style={{ color: '#8B99AE', fontSize: '13px', margin: 0 }}>ביטויים שהאתר מדורג עליהם ב-Search Console + נתוני Google Ads</p>
        <button onClick={run} disabled={running} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: running ? 'wait' : 'pointer', background: running ? '#1E293B' : '#3B82F6', color: '#fff', fontWeight: 600, fontSize: '13px', fontFamily: 'inherit' }}>
          {running ? '⏳ רץ...' : '🔄 עדכן נתונים'}
        </button>
      </div>

      {status && <div style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', background: 'rgba(59,130,246,0.1)', color: '#3B82F6', fontSize: '13px' }}>{status}</div>}

      {activeSession && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '16px' }}>
            {[
              { l: 'סה"כ', v: keywords.length, c: '#3B82F6' },
              { l: 'Striking', v: striking.length, c: '#F59E0B' },
              { l: 'עם Volume', v: keywords.filter(k => k.final_volume).length, c: '#22C55E' },
              { l: 'High Priority', v: keywords.filter(k => k.priority === 'high').length, c: '#EF4444' },
            ].map((s, i) => (
              <div key={i} style={{ background: '#111827', borderRadius: '8px', padding: '12px', border: '1px solid #1E293B' }}>
                <div style={{ fontSize: '11px', color: '#8B99AE' }}>{s.l}</div>
                <div style={{ fontSize: '22px', fontWeight: 800, color: s.c, fontFamily: "'JetBrains Mono', monospace" }}>{s.v.toLocaleString()}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
            {[['all', 'כל הביטויים'], ['striking', 'Striking Distance']].map(([k, l]) => (
              <button key={k} onClick={() => setSubTab(k)} style={{ padding: '6px 14px', border: 'none', borderBottom: subTab === k ? '2px solid #3B82F6' : '2px solid transparent', background: 'none', color: subTab === k ? '#3B82F6' : '#8B99AE', cursor: 'pointer', fontWeight: subTab === k ? 600 : 400, fontSize: '13px', fontFamily: 'inherit' }}>{l}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input placeholder="חיפוש..." value={search} onChange={e => setSearch(e.target.value)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #1E293B', background: '#111827', color: '#E2E8F0', fontSize: '13px', fontFamily: 'inherit', width: '180px' }} />
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #1E293B', background: '#111827', color: '#E2E8F0', fontSize: '13px', fontFamily: 'inherit' }}>
              <option value="">כל הסוגים</option>
              <option value="striking_distance">Striking</option>
              <option value="top_ranking">Top</option>
              <option value="existing">קיים</option>
            </select>
            <span style={{ fontSize: '12px', color: '#4B5C73', alignSelf: 'center' }}>{list.length} תוצאות</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ background: '#0D1117' }}>
                <TH label="ביטוי" field="keyword" w="30%" />
                <TH label="מיקום" field="sc_position" w="70px" />
                <TH label="נפח" field="final_volume" w="80px" />
                <TH label="CPC" field="final_cpc" w="70px" />
                <TH label="קליקים" field="sc_clicks" w="70px" />
                <TH label="חשיפות" field="sc_impressions" w="80px" />
                <TH label="ציון" field="opportunity_score" w="90px" />
                <th style={{ padding: '10px 8px', fontSize: '12px', color: '#8B99AE', borderBottom: '2px solid #1E293B', width: '70px' }}>סוג</th>
              </tr></thead>
              <tbody>
                {list.slice(0, 200).map((k, i) => {
                  const ts = typeStyle[k.keyword_type] || { l: '-', c: '#8B99AE' };
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #1E293B' }} title={k.recommendation || ''}>
                      <td style={{ padding: '10px 8px', fontWeight: 500 }}>{k.keyword}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>{fmtPos(k.sc_position)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", color: k.final_volume ? '#E2E8F0' : '#4B5C73' }}>{k.final_volume?.toLocaleString() || '-'}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", color: '#8B99AE' }}>{k.final_cpc ? '₪' + k.final_cpc.toFixed(0) : '-'}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace" }}>{k.sc_clicks?.toLocaleString() || '-'}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace" }}>{k.sc_impressions?.toLocaleString() || '-'}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div style={{ width: '36px', height: '5px', background: '#1E293B', borderRadius: '3px', overflow: 'hidden' }}><div style={{ width: (k.opportunity_score || 0) + '%', height: '100%', background: k.opportunity_score >= 65 ? '#22C55E' : k.opportunity_score >= 40 ? '#F59E0B' : '#6B7280', borderRadius: '3px' }} /></div>
                          <span style={{ fontSize: '12px', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: k.opportunity_score >= 65 ? '#22C55E' : k.opportunity_score >= 40 ? '#F59E0B' : '#6B7280' }}>{k.opportunity_score}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}><span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: ts.c + '18', color: ts.c }}>{ts.l}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!activeSession && !running && (
        <div style={{ padding: '50px', textAlign: 'center', color: '#4B5C73' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📊</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#8B99AE' }}>לחץ "עדכן נתונים" כדי לשלוף ביטויים מ-Search Console</div>
        </div>
      )}
    </div>
  );
}

/* ─── Discovery Tab (new research) ─── */
function Discovery({ siteId }) {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [mapping, setMapping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ business_name: '', business_description: '', core_topics: '', competitors: '' });

  useEffect(() => { load(); }, [siteId]);

  async function load() {
    try {
      const data = await api.getDiscoverySessions(siteId);
      setSessions(data.sessions || []);
      const done = (data.sessions || []).find(s => s.status === 'completed');
      if (done) { await loadResults(done.id); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function loadResults(sid) {
    const data = await api.getDiscoveryResults(sid);
    setActiveSession(data);
    setGroups(data.groups || []);
    if (data.selected_keywords?.length) setSelected(new Set(data.selected_keywords));
    if (data.keyword_mapping?.length) setMapping(data.keyword_mapping);
  }

  async function run() {
    const topics = form.core_topics.split(',').map(t => t.trim()).filter(Boolean);
    const comps = form.competitors.split(',').map(t => t.trim()).filter(Boolean);

    setRunning(true); setStatus('Claude מנתח את התחום ומייצר ביטויים...'); setShowForm(false);
    try {
      const r = await api.runDiscovery({
        site_id: parseInt(siteId),
        business_name: form.business_name,
        business_description: form.business_description,
        core_topics: topics.length ? topics : null,
        competitor_domains: comps.length ? comps : null,
        enrich_gads: true,
      });

      const poll = setInterval(async () => {
        const s = await api.getDiscoveryStatus(r.session_id);
        if (s.status === 'enriching') setStatus('משלים נתוני Volume ו-CPC מ-Google Ads...');
        if (s.status === 'completed') {
          clearInterval(poll); setStatus(null); setRunning(false);
          await loadResults(r.session_id); await load();
        } else if (s.status === 'failed') {
          clearInterval(poll); setStatus('שגיאה: ' + s.error_message); setRunning(false);
        }
      }, 3000);
    } catch (e) { setStatus('שגיאה: ' + e.message); setRunning(false); }
  }

  function toggleSelect(kw) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(kw) ? next.delete(kw) : next.add(kw);
      return next;
    });
  }

  function selectAllInGroup(groupKws) {
    setSelected(prev => {
      const next = new Set(prev);
      groupKws.forEach(k => next.add(k.keyword));
      return next;
    });
  }

  async function handleSelectKeywords() {
    if (!activeSession || selected.size === 0) return;
    setStatus('ממפה ביטויים לעמודים...');
    try {
      const res = await api.selectDiscoveryKeywords(activeSession.session_id, Array.from(selected));
      setMapping(res.mapping);
      setStatus(`נבחרו ${res.selected} ביטויים: ${res.with_page} עם עמוד, ${res.need_page} צריכים עמוד חדש`);
      setTimeout(() => setStatus(null), 5000);
    } catch (e) { setStatus('שגיאה: ' + e.message); }
  }

  async function handleRate(gi, ki, rating) {
    if (!activeSession) return;
    try {
      await api.rateDiscoveryKeyword(activeSession.session_id, gi, ki, rating);
      const updated = [...groups];
      updated[gi].keywords[ki].rating = rating;
      setGroups(updated);
    } catch (e) { console.error(e); }
  }

  const totalKw = groups.reduce((s, g) => s + (g.keywords?.length || 0), 0);
  const withVolume = groups.reduce((s, g) => s + (g.keywords?.filter(k => k.volume)?.length || 0), 0);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#8B99AE' }}>טוען...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <p style={{ color: '#8B99AE', fontSize: '13px', margin: 0 }}>מחקר מקיף מאפס - Claude מייצר קבוצות נושאים וביטויים, Google Ads מעשיר volume ו-CPC</p>
        <div style={{ display: 'flex', gap: '8px' }}>
          {activeSession && (
            <>
              <button onClick={async () => { try { const r = await api.enrichIntitle(activeSession.session_id); setStatus(r.message || 'Intitle check running...'); setTimeout(() => setStatus(null), 5000); } catch(e) { setStatus('שגיאה: ' + e.message); }}} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #1E293B', background: '#111827', color: '#8B99AE', fontWeight: 600, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                🔢 בדוק Intitle
              </button>
              <a href={(process.env.PUBLIC_URL || '') + '/api/keyword-discovery/' + activeSession.session_id + '/export?format=xlsx'} download style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #1E293B', background: '#111827', color: '#8B99AE', fontWeight: 600, fontSize: '13px', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                📥 ייצוא Excel
              </a>
            </>
          )}
          <button onClick={() => setShowForm(!showForm)} disabled={running} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: running ? '#1E293B' : 'linear-gradient(135deg, #A855F7, #6366F1)', color: '#fff', fontWeight: 600, fontSize: '13px', fontFamily: 'inherit' }}>
            {running ? '⏳ רץ...' : '✨ מחקר חדש'}
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ background: '#111827', border: '1px solid #1E293B', borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#8B99AE', display: 'block', marginBottom: '4px' }}>שם העסק</label>
              <input value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} placeholder="למשל: כתום פרגולות" style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #1E293B', background: '#0D1117', color: '#E2E8F0', fontSize: '14px', fontFamily: 'inherit' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#8B99AE', display: 'block', marginBottom: '4px' }}>נושאי ליבה (מופרדים בפסיק)</label>
              <input value={form.core_topics} onChange={e => setForm({ ...form, core_topics: e.target.value })} placeholder="פרגולות, סוככים, גדרות" style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #1E293B', background: '#0D1117', color: '#E2E8F0', fontSize: '14px', fontFamily: 'inherit' }} />
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '12px', color: '#8B99AE', display: 'block', marginBottom: '4px' }}>תיאור העסק</label>
            <textarea value={form.business_description} onChange={e => setForm({ ...form, business_description: e.target.value })} rows={3} placeholder="תיאור מפורט של העסק, השירותים, קהל יעד, אזור שירות..." style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #1E293B', background: '#0D1117', color: '#E2E8F0', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', color: '#8B99AE', display: 'block', marginBottom: '4px' }}>מתחרים (דומיינים, מופרדים בפסיק)</label>
            <input value={form.competitors} onChange={e => setForm({ ...form, competitors: e.target.value })} placeholder="competitor1.co.il, competitor2.co.il" style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #1E293B', background: '#0D1117', color: '#E2E8F0', fontSize: '14px', fontFamily: 'inherit' }} />
          </div>
          <button onClick={run} disabled={!form.business_name || !form.business_description || running} style={{ padding: '10px 28px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: '#A855F7', color: '#fff', fontWeight: 600, fontSize: '14px', fontFamily: 'inherit', opacity: (!form.business_name || !form.business_description) ? 0.5 : 1 }}>
            🚀 התחל מחקר
          </button>
        </div>
      )}

      {status && <div style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', background: status.includes('שגיאה') ? 'rgba(239,68,68,0.1)' : 'rgba(168,85,247,0.1)', color: status.includes('שגיאה') ? '#EF4444' : '#A855F7', fontSize: '13px' }}>{status}</div>}

      {/* Stats */}
      {activeSession && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '16px' }}>
          {[
            { l: 'קבוצות', v: groups.length, c: '#A855F7' },
            { l: 'ביטויים', v: totalKw, c: '#3B82F6' },
            { l: 'עם Volume', v: withVolume, c: '#22C55E' },
            { l: 'נבחרו לקידום', v: selected.size, c: '#F59E0B' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#111827', borderRadius: '8px', padding: '12px', border: '1px solid #1E293B' }}>
              <div style={{ fontSize: '11px', color: '#8B99AE' }}>{s.l}</div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: s.c, fontFamily: "'JetBrains Mono', monospace" }}>{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Select button */}
      {selected.size > 0 && !mapping && (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', padding: '12px 16px', background: 'rgba(245,158,11,0.08)', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.2)' }}>
          <span style={{ color: '#F59E0B', fontWeight: 600, fontSize: '14px' }}>{selected.size} ביטויים נבחרו</span>
          <button onClick={handleSelectKeywords} style={{ padding: '6px 16px', borderRadius: '6px', border: 'none', background: '#F59E0B', color: '#000', fontWeight: 600, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
            מפה לעמודים ←
          </button>
        </div>
      )}

      {/* Mapping results */}
      {mapping && (
        <div style={{ marginBottom: '20px', padding: '16px', background: '#111827', borderRadius: '10px', border: '1px solid #1E293B' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>📍 מיפוי ביטויים לעמודים</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <div style={{ padding: '10px', background: 'rgba(34,197,94,0.08)', borderRadius: '6px' }}>
              <span style={{ color: '#22C55E', fontWeight: 700, fontSize: '20px' }}>{mapping.filter(m => m.has_page).length}</span>
              <span style={{ color: '#8B99AE', fontSize: '13px', marginRight: '8px' }}> יש עמוד - אופטימיזציה</span>
            </div>
            <div style={{ padding: '10px', background: 'rgba(239,68,68,0.08)', borderRadius: '6px' }}>
              <span style={{ color: '#EF4444', fontWeight: 700, fontSize: '20px' }}>{mapping.filter(m => m.has_page === false).length}</span>
              <span style={{ color: '#8B99AE', fontSize: '13px', marginRight: '8px' }}> חסר עמוד - צריך ליצור</span>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#0D1117' }}>
              <th style={{ padding: '8px', textAlign: 'right', color: '#8B99AE', borderBottom: '2px solid #1E293B' }}>ביטוי</th>
              <th style={{ padding: '8px', textAlign: 'center', color: '#8B99AE', borderBottom: '2px solid #1E293B', width: '80px' }}>סטטוס</th>
              <th style={{ padding: '8px', textAlign: 'center', color: '#8B99AE', borderBottom: '2px solid #1E293B', width: '70px' }}>מיקום</th>
              <th style={{ padding: '8px', textAlign: 'right', color: '#8B99AE', borderBottom: '2px solid #1E293B' }}>עמוד / פעולה</th>
            </tr></thead>
            <tbody>
              {mapping.map((m, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #1E293B' }}>
                  <td style={{ padding: '8px', fontWeight: 500 }}>{m.keyword}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: m.has_page ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: m.has_page ? '#22C55E' : '#EF4444' }}>
                      {m.has_page ? '✓ קיים' : '✗ חסר'}
                    </span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", color: m.position ? '#F59E0B' : '#4B5C73' }}>{m.position ? m.position.toFixed(1) : '-'}</td>
                  <td style={{ padding: '8px', fontSize: '12px', color: '#8B99AE', direction: 'ltr', textAlign: 'left' }}>{m.has_page ? (m.url || '').replace(/https?:\/\/[^\/]+/, '') : <span style={{ color: '#EF4444' }}>צריך ליצור עמוד</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Groups */}
      {groups.map((g, gi) => {
        const kws = (g.keywords || []).filter(k => !search || k.keyword?.includes(search));
        if (kws.length === 0 && search) return null;
        return (
          <div key={gi} style={{ marginBottom: '16px', background: '#111827', borderRadius: '10px', border: '1px solid #1E293B', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #1E293B', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: '15px' }}>{g.name}</span>
                <span style={{ color: '#4B5C73', fontSize: '12px', marginRight: '8px' }}> ({kws.length} ביטויים)</span>
                {g.description && <div style={{ fontSize: '12px', color: '#8B99AE', marginTop: '2px' }}>{g.description}</div>}
              </div>
              <button onClick={() => selectAllInGroup(kws)} style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #1E293B', background: 'none', color: '#8B99AE', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>בחר הכל</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr>
                <th style={{ padding: '8px', width: '40px' }}></th>
                <th style={{ padding: '8px', textAlign: 'right', color: '#8B99AE', fontSize: '11px' }}>ביטוי</th>
                <th style={{ padding: '8px', textAlign: 'center', color: '#8B99AE', fontSize: '11px', width: '80px' }}>נפח</th>
                <th style={{ padding: '8px', textAlign: 'center', color: '#8B99AE', fontSize: '11px', width: '70px' }}>CPC</th>
                <th style={{ padding: '8px', textAlign: 'center', color: '#8B99AE', fontSize: '11px', width: '80px' }}>תחרות</th>
                <th style={{ padding: '8px', textAlign: 'center', color: '#8B99AE', fontSize: '11px', width: '120px' }}>דירוג (0-5)</th>
              </tr></thead>
              <tbody>
                {kws.map((k, ki) => {
                  const isSelected = selected.has(k.keyword);
                  return (
                    <tr key={ki} style={{ borderTop: '1px solid #1E293B', background: isSelected ? 'rgba(245,158,11,0.05)' : 'transparent' }}>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(k.keyword)} style={{ cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: '8px', fontWeight: 500 }}>{k.keyword}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", color: k.volume ? '#E2E8F0' : '#4B5C73' }}>{k.volume?.toLocaleString() || '-'}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", color: '#8B99AE' }}>{k.cpc ? '₪' + k.cpc.toFixed(1) : '-'}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {k.competition && <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: k.competition === 'HIGH' ? 'rgba(239,68,68,0.12)' : k.competition === 'MEDIUM' ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)', color: k.competition === 'HIGH' ? '#EF4444' : k.competition === 'MEDIUM' ? '#F59E0B' : '#22C55E' }}>{k.competition}</span>}
                        {!k.competition && <span style={{ color: '#4B5C73' }}>-</span>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '2px' }}>
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} onClick={() => handleRate(gi, ki, star)} style={{ cursor: 'pointer', fontSize: '16px', color: (k.rating || 0) >= star ? '#F59E0B' : '#2D3A4F' }}>★</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Empty state */}
      {!activeSession && !running && (
        <div style={{ padding: '50px', textAlign: 'center', color: '#4B5C73' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>✨</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#8B99AE' }}>לחץ "מחקר חדש" כדי לייצר מחקר ביטויים מקיף לתחום</div>
          <div style={{ fontSize: '13px', color: '#4B5C73', marginTop: '6px' }}>Claude מנתח את התחום, מייצר קבוצות נושאים וביטויים, Google Ads מעשיר נתונים</div>
        </div>
      )}
    </div>
  );
}

/* ─── Main Component ─── */
export default function SiteKeywordResearch() {
  const { id } = useParams();
  const [mode, setMode] = useState('sc');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>🔬 מחקר מילים</h2>
      </div>

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '24px', background: '#111827', borderRadius: '10px', padding: '4px', border: '1px solid #1E293B', width: 'fit-content' }}>
        <button onClick={() => setMode('sc')} style={{
          padding: '10px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer',
          background: mode === 'sc' ? '#3B82F6' : 'transparent',
          color: mode === 'sc' ? '#fff' : '#8B99AE',
          fontWeight: 600, fontSize: '14px', fontFamily: 'inherit', transition: 'all 0.2s',
        }}>
          📊 ניתוח SC
        </button>
        <button onClick={() => setMode('discovery')} style={{
          padding: '10px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer',
          background: mode === 'discovery' ? '#A855F7' : 'transparent',
          color: mode === 'discovery' ? '#fff' : '#8B99AE',
          fontWeight: 600, fontSize: '14px', fontFamily: 'inherit', transition: 'all 0.2s',
        }}>
          ✨ מחקר חדש
        </button>
      </div>

      {mode === 'sc' ? <SCAnalysis siteId={id} /> : <Discovery siteId={id} />}
    </div>
  );
}
